"use strict";

import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    CircularProgress,
    Grid,
    Avatar,
    Paper,
    FormControl,
    Select,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { useQuery, useMutation } from "@apollo/client";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { useNavigate } from "react-router-dom";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import { UserContext } from "../../../../../platform/components/auth/auth-context";

import ResidentDetailHorizontalTab from "./residentDetailHorizontalTab";
import AddEditResidentDialog from "./addResidentDialog";

import {
    DEACTIVATE_RESIDENT,
    GET_RESIDENTS,
    POST_RESIDENT,
    UPDATE_RESIDENT,
} from "../../../resident";

import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../../../platform/graphql/queries";
import { getEffectivePlan, getTrialDaysLeft } from "../../../../../platform/util/utils";
import { ResidentStatus } from "./utils/utils";

/* ===============================
   PRICING (unchanged)
=============================== */

export default function ResidentListVerticalTab() {
    const { providerUser } = React.useContext(UserContext);
    const selectedBusiness = useSelectedBusiness();
    const businessId = selectedBusiness?.businessBasic?.id;
    const [pendingSelectResidentId, setPendingSelectResidentId] = useState(null);
    const [pendingRemoveResidentId, setPendingRemoveResidentId] = useState(null);
    const navigate = useNavigate();

    /* ===============================
         DATA
      =============================== */
    const {
        data: residentsData,
        loading: residentsLoading,
        refetch: refetchResidents,
    } = useQuery(GET_RESIDENTS, {
        variables: { businessId },
        skip: !businessId,
        fetchPolicy: "network-only",
    });
    const queriedResidents = residentsData?.getResidents || [];
    const cachedResidents = selectedBusiness?.residents || [];
    const allResidents = useMemo(() => {
        const merged = [];
        const seen = new Set();

        queriedResidents.forEach((resident) => {
            if (!resident?.id || seen.has(resident.id)) return;
            seen.add(resident.id);
            merged.push(resident);
        });

        cachedResidents.forEach((resident) => {
            if (!resident?.id || seen.has(resident.id)) return;
            seen.add(resident.id);
            merged.push(resident);
        });

        return merged;
    }, [queriedResidents, cachedResidents]);

    const { data: billingData, loading: billingLoading } = useQuery(
        GET_BILLING_SUBSCRIPTION,
        {
            variables: { businessId },
            skip: !businessId,
            fetchPolicy: "network-only",
        },
    );
    const { data: plansData } = useQuery(GET_PLANS, {
        skip: !businessId,
    });

    const billingSub = billingData?.getBillingSubscription;
    const hasSeatBackedSubscription =
        billingSub?.status === "active" || billingSub?.status === "canceling";
    const hasActiveSubscription = billingSub?.status === "active";
    const currentPlan = Number(providerUser?.planId || 0);
    const effectivePlan = Number(getEffectivePlan(providerUser || {}) || 0);
    const hasSeatAccessOverride = effectivePlan > currentPlan;

    const paidSeats = hasSeatBackedSubscription
        ? Number(billingSub.residentSeats || 0)
        : 0;

    const usedSeats = allResidents.filter(
        (r) => r.status === ResidentStatus.ACTIVE,
    ).length;

    const remainingSeats = hasSeatAccessOverride
        ? Number.MAX_SAFE_INTEGER
        : hasActiveSubscription
            ? paidSeats - usedSeats
            : null;

    /* ===============================
         FILTER
      =============================== */
    const [statusFilter, setStatusFilter] = useState("ACTIVE");

    const residents = useMemo(() => {
        if (statusFilter === "ALL") return allResidents;
        if (statusFilter === "ACTIVE")
            return allResidents.filter((r) => r.status === ResidentStatus.ACTIVE);
        return allResidents.filter((r) => r.status === ResidentStatus.INACTIVE);
    }, [allResidents, statusFilter]);
    const switchResidents = useMemo(() => residents, [residents]);

    /* ===============================
         STATE
      =============================== */
    const [selectedResident, setSelectedResident] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [residentFilter, setResidentFilter] = useState("");

    const [openDialog, setOpenDialog] = useState(false);
    const [openInactivateDialog, setOpenInactivateDialog] = useState(false);
    const [openSeatLimitDialog, setOpenSeatLimitDialog] = useState(false);
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const [actionMessage, setActionMessage] = useState("");
    const [actionError, setActionError] = useState("");

    const [isEditMode, setIsEditMode] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    // 🔑 CRITICAL FIX: force dialog remount
    const [dialogKey, setDialogKey] = useState("add");

    /* ===============================
         MUTATIONS
      =============================== */
    const [postResident] = useMutation(POST_RESIDENT);
    const [updateResident] = useMutation(UPDATE_RESIDENT);
    const [deactivateResident] = useMutation(DEACTIVATE_RESIDENT);
    const standardPlanId = Number(
        plansData?.plans?.find((plan) => plan.code === "STANDARD")?.id || 2
    );
    const trialDaysLeft = getTrialDaysLeft(providerUser || {});
    const showTrialEndingSoon = Number(trialDaysLeft) > 0 && Number(trialDaysLeft) <= 5;
    const isSelectedInactive =
        selectedResident?.status === ResidentStatus.INACTIVE;

    const viewableActiveResidentIds = useMemo(() => {
        const activeResidents = allResidents.filter(
            (r) => r.status === ResidentStatus.ACTIVE
        );
        const allowedCount = hasSeatAccessOverride
            ? activeResidents.length
            : Math.max(0, Number(paidSeats || 0));
        return new Set(activeResidents.slice(0, allowedCount).map((r) => r.id));
    }, [allResidents, paidSeats, hasSeatAccessOverride]);

    const canViewResident = useMemo(
        () => (resident) =>
            Boolean(
                resident &&
                (
                    resident.status !== ResidentStatus.ACTIVE ||
                    viewableActiveResidentIds.has(resident.id)
                )
            ),
        [viewableActiveResidentIds]
    );

    const viewableResidents = useMemo(
        () => switchResidents.filter((resident) => canViewResident(resident)),
        [switchResidents, canViewResident]
    );

    const filteredSwitchResidents = useMemo(() => {
        const q = residentFilter.trim().toLowerCase();
        if (!q) return switchResidents;
        return switchResidents.filter((r) =>
            `${r?.firstName || ""} ${r?.lastName || ""}`.toLowerCase().includes(q)
        );
    }, [switchResidents, residentFilter]);

    /* ===============================
         SELECTION SAFETY
      =============================== */
    useEffect(() => {
        if (!switchResidents.length) {
            setSelectedResident(null);
            setSelectedIndex(0);
            return;
        }

        // 🔑 PRIORITY: re-select edited resident
        if (
            pendingRemoveResidentId &&
            selectedResident?.id === pendingRemoveResidentId
        ) {
            setSelectedResident(switchResidents[0]);
            setSelectedIndex(0);
            setPendingRemoveResidentId(null);
            return;
        }

        // 🔑 PRIORITY: re-select edited resident
        if (pendingSelectResidentId) {
            const idx = switchResidents.findIndex((r) => r.id === pendingSelectResidentId);

            if (idx !== -1) {
                setSelectedResident(switchResidents[idx]);
                setSelectedIndex(idx);
                setPendingSelectResidentId(null);
                return;
            }
        }

        // keep selection if still valid in filtered list and seat allowance
        if (
            selectedResident &&
            switchResidents.some((r) => r.id === selectedResident.id) &&
            canViewResident(selectedResident)
        ) {
            return;
        }

        // fallback to first resident within seat allowance
        if (viewableResidents.length > 0) {
            setSelectedResident(viewableResidents[0]);
            setSelectedIndex(
                Math.max(
                    0,
                    switchResidents.findIndex((r) => r.id === viewableResidents[0].id)
                )
            );
            return;
        }

        // no viewable residents in current seat allowance
        setSelectedResident(switchResidents[0]);
        setSelectedIndex(0);
    }, [
        switchResidents,
        viewableResidents,
        canViewResident,
        selectedResident,
        pendingSelectResidentId,
        pendingRemoveResidentId,
    ]);

    useEffect(() => {
        if (!selectedResident) return;
        const idx = switchResidents.findIndex((r) => r.id === selectedResident.id);
        if (idx !== -1 && idx !== selectedIndex) {
            setSelectedIndex(idx);
        }
    }, [selectedResident, switchResidents]);

    useEffect(() => {
        if (!selectedResident) return;
        const refreshedResident = switchResidents.find(
            (resident) => resident.id === selectedResident.id
        );
        if (refreshedResident && refreshedResident !== selectedResident) {
            setSelectedResident(refreshedResident);
        }
    }, [switchResidents, selectedResident]);

    /* ===============================
         HELPERS
      =============================== */
    const getInitials = (f, l) =>
        `${f?.[0] || ""}${l?.[0] || ""}`.toUpperCase() || "?";

    /* ===============================
         HANDLERS
      =============================== */
    const handleOpenAddResidentDialog = () => {
        if (!hasSeatAccessOverride && !hasActiveSubscription) {
            setCheckPlanNeeded(true);
            return;
        }
        if (remainingSeats <= 0) {
            setOpenSeatLimitDialog(true);
            return;
        }

        setIsEditMode(false);
        setDialogKey(`add-${Date.now()}`); // 🔥 FORCE NEW INSTANCE
        setOpenDialog(true);
    };

    const handleEditResident = () => {
        if (!selectedResident) return;

        setIsEditMode(true);
        setDialogKey(`edit-${selectedResident.id}-${Date.now()}`); // 🔥 FORCE REMOUNT
        setOpenDialog(true);
    };

    const handleSaveResident = async (form) => {
        try {
            if (isEditMode) {
                const editedId = selectedResident.id;

                await updateResident({
                    variables: {
                        id: editedId,
                        data: {
                            ...form,
                            businessId, // required by schema
                        },
                    },
                });

                // 🔑 remember who was edited
                setPendingSelectResidentId(editedId);
            } else {
                await postResident({
                    variables: {
                        data: {
                            ...form,
                            businessId,
                        },
                    },
                });
            }

            setOpenDialog(false);
            setIsEditMode(false);

            await refetchResidents(); // 🔁 fetch fresh data
        } catch (err) {
            console.error("Save resident failed:", err);
        }
    };

    const handleConfirmInactivateResident = async () => {
        try {
            const removedId = selectedResident.id;
            await deactivateResident({
                variables: {
                    id: selectedResident.id,
                },
            });

            setPendingRemoveResidentId(removedId);
            setOpenInactivateDialog(false);
            await refetchResidents();
        } catch (err) {
            console.error("Inactivate resident failed:", err);
        }
    };

    const handleReactivateResident = async () => {
        if (!selectedResident) return;
        if (!hasSeatAccessOverride && !hasActiveSubscription) {
            setCheckPlanNeeded(true);
            return;
        }
        if (remainingSeats <= 0) {
            setOpenSeatLimitDialog(true);
            return;
        }

        try {
            const reactivatedId = selectedResident.id;
            await updateResident({
                variables: {
                    id: reactivatedId,
                    data: {
                        firstName: selectedResident?.firstName || "",
                        lastName: selectedResident?.lastName || "",
                        gender: selectedResident?.gender || "",
                        dateOfBirth: selectedResident?.dateOfBirth || null,
                        admissionDate: selectedResident?.admissionDate || null,
                        dischargeDate: selectedResident?.dischargeDate || null,
                        status: ResidentStatus.ACTIVE,
                        businessId,
                    },
                },
            });

            setPendingSelectResidentId(reactivatedId);
            setActionError("");
            setActionMessage("Resident reactivated successfully and moved to Active.");
            setStatusFilter("ACTIVE");
            await refetchResidents();
        } catch (err) {
            console.error("Reactivate resident failed:", err);
            setActionMessage("");
            setActionError(err?.message || "Unable to reactivate resident. Please try again.");
        }
    };

    /* ===============================
         LOADING
      =============================== */
    if (!businessId || residentsLoading || billingLoading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    /* ===============================
         RENDER
      =============================== */
    return (
        <>
            {checkPlanNeeded && (
                <UpgradePlan
                    featureName="Resident management"
                    requiredPlanId={standardPlanId}
                    onClose={() => setCheckPlanNeeded(false)}
                />
            )}
            {showTrialEndingSoon && (
                <Box sx={{ px: { xs: 1.5, sm: 3, md: 5 }, pt: 1.5 }}>
                    <Alert severity="warning">
                        Trial period ends in {trialDaysLeft} day{Number(trialDaysLeft) === 1 ? "" : "s"}.
                        Resident access limits will apply after expiry.
                    </Alert>
                </Box>
            )}
            {/* ================= HEADER ================= */}
            <div className="content-header">
                <div className="content-head">
                    <div
                        className="top"
                        style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Residents
                        </Typography>

                        <Box display="flex" alignItems="center" gap={1.5}>
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAddResidentDialog}
                            >
                                Add Resident
                            </Button>

                            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                                <MoreVertIcon />
                            </IconButton>

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleEditResident(); // ✅ FIXED
                                        setAnchorEl(null);
                                    }}
                                    disabled={isSelectedInactive}
                                >
                                    Edit
                                </MenuItem>

                                <MenuItem
                                    onClick={() => {
                                        setAnchorEl(null);
                                        setOpenInactivateDialog(true);
                                    }}
                                    disabled={
                                        selectedResident?.status === ResidentStatus.INACTIVE
                                    }
                                >
                                    Inactivate
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setAnchorEl(null);
                                        handleReactivateResident();
                                    }}
                                    disabled={!isSelectedInactive}
                                >
                                    Reactivate
                                </MenuItem>
                            </Menu>
                        </Box>
                    </div>
                </div>
            </div>

            {/* ================= BODY ================= */}
            <Box
                sx={{
                    flexGrow: 1,
                    width: "100%",
                    boxSizing: "border-box",
                    px: { xs: 1.5, sm: 3, md: 5 },
                    py: { xs: 1, sm: 3, md: 5 },
                }}
            >
                <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                border: (t) => `1px solid ${t.palette.divider}`,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                            }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search resident..."
                                value={residentFilter}
                                onChange={(e) => setResidentFilter(e.target.value)}
                                sx={{ mb: 1 }}
                            />
                            {!!actionMessage && (
                                <Alert severity="success" sx={{ width: "100%", mb: 0.5 }}>
                                    {actionMessage}
                                </Alert>
                            )}
                            {!!actionError && (
                                <Alert severity="error" sx={{ width: "100%", mb: 0.5 }}>
                                    {actionError}
                                </Alert>
                            )}
                            {statusFilter === "INACTIVE" && (
                                <Alert severity="info" sx={{ width: "100%", mb: 0.5 }}>
                                    Viewing inactive residents. Reactivating any resident will use an active seat.
                                </Alert>
                            )}
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(2, minmax(0, 1fr))",
                                        sm: "repeat(auto-fill, minmax(160px, 1fr))",
                                    },
                                    gap: 1,
                                    maxHeight: { xs: 320, sm: 360 },
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    pb: 0.5,
                                }}
                            >
                                {filteredSwitchResidents.map((r) => {
                                    const isActive = selectedResident?.id === r.id;
                                    const canView = canViewResident(r);
                                    const idx = switchResidents.findIndex((x) => x.id === r.id);
                                    const statusText =
                                        Object.keys(ResidentStatus).find(
                                            (k) => ResidentStatus[k] === r.status
                                        ) || "Unknown";
                                    const showStatusLabel = r.status !== ResidentStatus.ACTIVE;

                                    return (
                                        <Button
                                            key={r.id}
                                            variant={isActive ? "contained" : "outlined"}
                                            disableRipple
                                            onClick={() => {
                                                setSelectedIndex(idx);
                                                setSelectedResident(r);
                                                if (!canView) setOpenSeatLimitDialog(true);
                                            }}
                                            sx={{
                                                width: "100%",
                                                minWidth: 0,
                                                px: 1,
                                                py: 0.6,
                                                borderRadius: 2.25,
                                                textTransform: "none",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "flex-start",
                                                gap: 0.4,
                                                bgcolor: isActive ? "#22374a" : "#fff",
                                                color: isActive ? "#fff" : "text.primary",
                                                borderColor: isActive ? "#22374a" : "#d5deea",
                                                WebkitTapHighlightColor: "transparent",
                                                boxShadow: isActive
                                                    ? "0 12px 24px rgba(34,55,74,0.28)"
                                                    : "0 4px 10px rgba(17,24,39,0.07)",
                                                transition:
                                                    "box-shadow 120ms ease, border-color 120ms ease, background-color 120ms ease",
                                                "&:hover": {
                                                    borderColor: "#22374a",
                                                    boxShadow: isActive
                                                        ? "0 14px 28px rgba(34,55,74,0.33)"
                                                        : "0 10px 20px rgba(17,24,39,0.12)",
                                                },
                                                "&:active": {
                                                    bgcolor: isActive ? "#22374a" : "#f3f7fc",
                                                    borderColor: "#22374a",
                                                },
                                                "&.Mui-focusVisible": {
                                                    bgcolor: isActive ? "#22374a" : "#f6f9fd",
                                                    borderColor: "#22374a",
                                                },
                                            }}
                                        >
                                            {r?.image?.url ? (
                                                <img
                                                    src={r.image.url}
                                                    alt={`${r.firstName} ${r.lastName}`}
                                                    draggable={false}
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        borderRadius: "50%",
                                                        objectFit: "cover",
                                                        userSelect: "none",
                                                        WebkitUserSelect: "none",
                                                        WebkitTouchCallout: "none",
                                                        pointerEvents: "none",
                                                        border: isActive
                                                            ? "2px solid rgba(255,255,255,0.95)"
                                                            : "2px solid rgba(35,55,74,0.18)",
                                                        boxShadow: "0 6px 14px rgba(15,23,42,0.2)",
                                                    }}
                                                />
                                            ) : (
                                                <Avatar
                                                    sx={{
                                                        width: 60,
                                                        height: 60,
                                                        fontSize: 18,
                                                        boxShadow: "0 6px 14px rgba(15,23,42,0.2)",
                                                        userSelect: "none",
                                                        WebkitUserSelect: "none",
                                                        WebkitTouchCallout: "none",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    {getInitials(r.firstName, r.lastName)}
                                                </Avatar>
                                            )}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    width: "100%",
                                                    textAlign: "center",
                                                    overflow: "hidden",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    fontWeight: isActive ? 800 : 700,
                                                    fontSize: 11.8,
                                                    lineHeight: 1.2,
                                                    minHeight: 20,
                                                }}
                                            >
                                                {r.firstName} {r.lastName}
                                            </Typography>
                                            {showStatusLabel && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: isActive ? "rgba(255,255,255,0.95)" : "#52657b",
                                                        fontWeight: 700,
                                                        fontSize: 10.2,
                                                        px: 0.75,
                                                        py: 0.12,
                                                        borderRadius: 999,
                                                        bgcolor: isActive ? "rgba(255,255,255,0.18)" : "#eef3f9",
                                                        letterSpacing: 0.2,
                                                    }}
                                                >
                                                    {statusText}
                                                </Typography>
                                            )}
                                            {!canView && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: isActive ? "rgba(255,255,255,0.9)" : "warning.main" }}
                                                >
                                                    Upgrade to view
                                                </Typography>
                                            )}
                                        </Button>
                                    );
                                })}
                                {filteredSwitchResidents.length === 0 && (
                                    <Box sx={{ gridColumn: "1 / -1", py: 2 }}>
                                        <Typography variant="body2" color="text.secondary" textAlign="center">
                                            No residents match your search.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {selectedResident && canViewResident(selectedResident) ? (
                            isSelectedInactive ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        border: (t) => `1px dashed ${t.palette.divider}`,
                                        borderRadius: 2,
                                    }}
                                >
                                    <Alert severity="info" sx={{ mb: 1.5 }}>
                                        This resident is inactive. Menus are disabled.
                                    </Alert>
                                    <Typography variant="body2" color="text.secondary">
                                        Use the menu (top-right) and choose Reactivate.
                                        Reactivation consumes an active seat.
                                    </Typography>
                                </Paper>
                            ) : (
                                <ResidentDetailHorizontalTab
                                    resident={selectedResident}
                                    businessId={businessId}
                                    onCarePlanSaved={refetchResidents}
                                />
                            )
                        ) : (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    border: (t) => `1px dashed ${t.palette.divider}`,
                                }}
                            >
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {hasSeatAccessOverride
                                        ? "Trial/override access is active. Residents are fully viewable during this access period."
                                        : `Your current plan allows viewing ${paidSeats} active resident${Number(paidSeats) === 1 ? "" : "s"}.`}
                                </Alert>
                                {!hasSeatAccessOverride && (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            To view additional residents, increase your resident seats in
                                            subscription.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => navigate("/biz/pricing")}
                                        >
                                            Manage Subscription
                                        </Button>
                                    </>
                                )}
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {/* ================= ADD / EDIT ================= */}
            <AddEditResidentDialog
                key={dialogKey}
                open={openDialog}
                handleClose={() => {
                    setOpenDialog(false);
                    setIsEditMode(false);
                }}
                handleSaveResident={handleSaveResident}
                resident={isEditMode ? selectedResident : null}
            />

            {/* ================= INACTIVATE CONFIRM ================= */}
            <Dialog
                open={openInactivateDialog}
                onClose={() => setOpenInactivateDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Inactivate Resident</DialogTitle>

                <DialogContent>
                    <Typography gutterBottom>
                        Are you sure you want to inactivate{" "}
                        <strong>
                            {selectedResident?.firstName} {selectedResident?.lastName}
                        </strong>
                        ?
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        This resident will <strong>not be deleted</strong>. Their
                        information will be kept for future reference and reporting, but
                        they will no longer be active.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenInactivateDialog(false)}>Cancel</Button>

                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmInactivateResident}
                    >
                        Inactivate Resident
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openSeatLimitDialog}
                onClose={() => setOpenSeatLimitDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Seat limit reached</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {hasSeatAccessOverride
                            ? "Trial/override access is active for this business."
                            : `You can currently view ${paidSeats} active resident${Number(paidSeats) === 1 ? "" : "s"} based on your purchased seats.`}
                    </Typography>
                    {!hasSeatAccessOverride && (
                        <Typography variant="body2" color="text.secondary">
                            Upgrade your subscription to increase seat count and view more residents.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSeatLimitDialog(false)}>Close</Button>
                    {!hasSeatAccessOverride && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setOpenSeatLimitDialog(false);
                                navigate("/biz/pricing");
                            }}
                        >
                            Upgrade Subscription
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

